import React, { createContext, ReactNode, useContext, useState } from 'react';
import {
    Employee,
    employees
} from '../data/employeeData';

interface EmployeeContextType {
  employees: Employee[];
  getEmployeeById: (id: string) => Employee | undefined;
  getEmployeesByDepartment: (department: string) => Employee[];
  getDirectReports: (managerId: string) => Employee[];
  getManagerChain: (employeeId: string) => Employee[];
  getDepartments: () => string[];
  getEmployeesByManager: (managerId: string) => Employee[];
  getOrgStats: () => {
    totalEmployees: number;
    departmentCounts: Record<string, number>;
    activeEmployees: number;
    avgTenure: number;
  };
  getManagerName: (managerId: string | null) => string;
  searchEmployees: (searchTerm: string) => Employee[];
  getOrganizationalHierarchy: () => OrganizationalNode[];
  addEmployee: (employee: Omit<Employee, 'id'>) => void;
}

interface OrganizationalNode {
  employee: Employee;
  children: OrganizationalNode[];
  level: number;
}

const EmployeeContext = createContext<EmployeeContextType | undefined>(undefined);

export const useEmployees = () => {
  const context = useContext(EmployeeContext);
  if (context === undefined) {
    throw new Error('useEmployees must be used within an EmployeeProvider');
  }
  return context;
};

interface EmployeeProviderProps {
  children: ReactNode;
}

export const EmployeeProvider: React.FC<EmployeeProviderProps> = ({ children }) => {
  const [employeeList, setEmployeeList] = useState<Employee[]>(employees);

  const getManagerName = (managerId: string | null): string => {
    if (!managerId) return 'None';
    const manager = employeeList.find(emp => emp.id === managerId);
    return manager ? manager.name : 'Unknown Manager';
  };

  const searchEmployees = (searchTerm: string): Employee[] => {
    const term = searchTerm.toLowerCase();
    return employeeList.filter(employee =>
      employee.name.toLowerCase().includes(term) ||
      employee.email.toLowerCase().includes(term) ||
      employee.department.toLowerCase().includes(term) ||
      employee.jobTitle.toLowerCase().includes(term) ||
      (employee.skills && employee.skills.some(skill => skill.toLowerCase().includes(term))) ||
      (employee.projects && employee.projects.some(project => project.toLowerCase().includes(term)))
    );
  };

  const buildHierarchyNode = (employee: Employee, level: number = 0): OrganizationalNode => {
    const directReports = employeeList.filter(emp => emp.managerId === employee.id);
    return {
      employee,
      level,
      children: directReports.map(report => buildHierarchyNode(report, level + 1))
    };
  };

  const getOrganizationalHierarchy = (): OrganizationalNode[] => {
    // Start with CEO and build the tree
    const ceo = employeeList.find(emp => emp.managerId === null);
    if (!ceo) return [];
    
    return [buildHierarchyNode(ceo)];
  };

  const addEmployee = (employeeData: Omit<Employee, 'id'>) => {
    console.log('🏢 EmployeeContext: Adding new employee', employeeData);
    const newEmployee: Employee = {
      ...employeeData,
      id: `emp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
    console.log('🆔 Generated employee ID:', newEmployee.id);
    console.log('📊 Current employee count:', employeeList.length);
    
    setEmployeeList(prev => {
      const updated = [...prev, newEmployee];
      console.log('✅ Employee list updated, new count:', updated.length);
      return updated;
    });
  };

  const value: EmployeeContextType = {
    employees: employeeList,
    getEmployeeById: (id: string) => employeeList.find(emp => emp.id === id),
    getEmployeesByDepartment: (department: string) => employeeList.filter(emp => emp.department === department),
    getDirectReports: (managerId: string) => employeeList.filter(emp => emp.managerId === managerId),
    getManagerChain: (employeeId: string) => {
      const chain: Employee[] = [];
      let currentEmployee = employeeList.find(emp => emp.id === employeeId);
      
      while (currentEmployee && currentEmployee.managerId) {
        const managerId = currentEmployee.managerId;
        const manager = employeeList.find(emp => emp.id === managerId);
        if (manager) {
          chain.push(manager);
          currentEmployee = manager;
        } else {
          break;
        }
      }
      
      return chain;
    },
    getDepartments: () => {
      const departmentSet = new Set(employeeList.map(emp => emp.department));
      return Array.from(departmentSet);
    },
    getEmployeesByManager: (managerId: string) => employeeList.filter(emp => emp.managerId === managerId),
    getOrgStats: () => {
      const departments = Array.from(new Set(employeeList.map(emp => emp.department)));
      const stats = {
        totalEmployees: employeeList.length,
        departmentCounts: {} as Record<string, number>,
        activeEmployees: employeeList.filter(emp => emp.employeeStatus === 'Active').length,
        avgTenure: 0
      };

      departments.forEach(dept => {
        stats.departmentCounts[dept] = employeeList.filter(emp => emp.department === dept).length;
      });

      return stats;
    },
    getManagerName,
    searchEmployees,
    getOrganizationalHierarchy,
    addEmployee
  };

  return (
    <EmployeeContext.Provider value={value}>
      {children}
    </EmployeeContext.Provider>
  );
};

export default EmployeeContext;
